import React from 'react';
import { Button, Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  getSupplierOrderDisplayNumber,
  type SupplierOrderPartyDto,
} from '../../../dto/supplierOrder';
import type { SupplierOrderStatus } from '../../../types/supplierOrder';
import { getSupplierOrderStatusColor } from '../supplierOrderStatuses';
import './SupplierOrderSummaryPreview.css';

const EMPTY_VALUE = '-';

export interface SupplierOrderSummarySource {
  orderNumber: string;
  displayOrderNumber?: string;
  useManualNumbering?: boolean;
  manualOrderNumber?: string;
  supplier?: SupplierOrderPartyDto;
  supplierName?: string | null;
  status?: SupplierOrderStatus;
}

interface SupplierOrderSummaryPreviewProps {
  order: SupplierOrderSummarySource;
  onOrderClick?: () => void;
}

export function getSupplierOrderSummary(order: SupplierOrderSummarySource): {
  displayNumber: string;
  supplierName: string;
} {
  return {
    displayNumber: order.displayOrderNumber || getSupplierOrderDisplayNumber(order),
    supplierName: order.supplierName || order.supplier?.name || EMPTY_VALUE,
  };
}

const SupplierOrderSummaryPreview: React.FC<SupplierOrderSummaryPreviewProps> = ({
  order,
  onOrderClick,
}) => {
  const { t } = useTranslation(['orders']);
  const summary = getSupplierOrderSummary(order);

  return (
    <div className="supplier-order-summary-preview">
      <span className="supplier-order-summary-preview__supplier">
        {summary.supplierName}
      </span>
      <div className="supplier-order-summary-preview__meta">
        {onOrderClick ? (
          <Button
            className="supplier-order-summary-preview__link"
            type="link"
            onClick={onOrderClick}
          >
            {summary.displayNumber}
          </Button>
        ) : (
          <span className="supplier-order-summary-preview__number">
            {summary.displayNumber}
          </span>
        )}
        {order.status ? (
          <Tag
            color={getSupplierOrderStatusColor(order.status)}
            className="supplier-order-summary-preview__status"
          >
            {t(`orders:supplierOrders.status.${order.status}`)}
          </Tag>
        ) : null}
      </div>
    </div>
  );
};

export default SupplierOrderSummaryPreview;
